import React from "react";

/**
 * Shared chrome for the billing modals.
 *
 * Replaces the per-modal copy of the panel markup, which had three problems: the
 * panel was 1100px wide for what is a two-column form (so the controls stretched
 * and the content looked sparse), the submit button sat at the end of the
 * scrolling content and slid out of reach once the audit fields were added, and
 * the header ended in a decorative vertical rule left over from the Figma export
 * that read as a rendering glitch.
 *
 * Now: a 720px column, a header that carries context about what is being edited,
 * a single scroll region, and a footer pinned to the bottom.
 */

function CloseButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Close"
      className="inline-flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-xl text-[#211f26] transition-colors hover:bg-[#f2eff3]"
    >
      <svg
        className="block size-5"
        fill="none"
        preserveAspectRatio="none"
        viewBox="0 0 24 24"
      >
        <path
          d="M18 6L6 18M6 6L18 18"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.5"
        />
      </svg>
    </button>
  );
}

export function ModalPanel({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative h-screen w-full">
      <div
        className="absolute box-border flex flex-col items-center pb-0 top-0"
        style={{
          left: "50%",
          paddingLeft: "clamp(12px, 3vw, 32px)",
          paddingRight: "clamp(12px, 3vw, 32px)",
          paddingTop: "16px",
          transform: "translateX(-50%)",
          height: "calc(100vh - 32px)",
          maxHeight: "calc(100vh - 32px)",
          minHeight: "0",
          width: "min(720px, calc(100vw - 24px))",
        }}
      >
        <div className="flex min-h-px w-full grow flex-col overflow-hidden rounded-[24px] border border-[#eae7ec] bg-[rgba(255,255,255,0.86)] shadow-[0px_24px_48px_-12px_rgba(33,31,38,0.18)] backdrop-blur-xl backdrop-filter">
          {children}
        </div>
      </div>
    </div>
  );
}

export default function BillingModalShell({
  title,
  subtitle,
  badge,
  onClose,
  footer,
  children,
}: {
  title: string;
  subtitle?: React.ReactNode;
  /** Short status pill next to the title, e.g. the plan currency. */
  badge?: React.ReactNode;
  onClose: () => void;
  footer?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <ModalPanel>
      <header className="flex shrink-0 flex-row items-start gap-4 border-b border-[#eae7ec] bg-[rgba(253,252,253,0.72)] px-6 py-5">
        <div className="min-w-px grow">
          <div className="flex flex-row items-center gap-2">
            <h2 className="font-['Geist:SemiBold',_sans-serif] text-[20px] font-semibold leading-[26px] tracking-[-0.2px] text-[#211f26]">
              {title}
            </h2>
            {badge}
          </div>
          {subtitle && (
            <div className="mt-1 font-['Geist:Regular',_sans-serif] text-[13px] leading-[18px] text-[#605d66]">
              {subtitle}
            </div>
          )}
        </div>
        <CloseButton onClick={onClose} />
      </header>

      <div className="billing-table-scroll min-h-px grow overflow-y-auto overflow-x-clip px-6 py-5">
        <div className="flex flex-col gap-5">{children}</div>
      </div>

      {footer && (
        // The shadow reads as "content continues above" — without it the scroll
        // region just stops at the border with no affordance.
        <footer className="shrink-0 border-t border-[#eae7ec] bg-[rgba(253,252,253,0.92)] px-6 py-4 shadow-[0_-8px_18px_rgba(33,31,38,0.06)]">
          {footer}
        </footer>
      )}
    </ModalPanel>
  );
}

/** Full-panel loading state, so it no longer reads as unstyled body text. */
export function ModalLoadingState({ message }: { message: string }) {
  return (
    <ModalPanel>
      <div className="flex grow flex-col items-center justify-center gap-3 py-16">
        <svg className="size-6 animate-spin text-[#2d58ff]" viewBox="0 0 24 24">
          <circle
            cx="12"
            cy="12"
            r="9"
            fill="none"
            stroke="currentColor"
            strokeOpacity="0.2"
            strokeWidth="2.5"
          />
          <path
            d="M21 12a9 9 0 0 0-9-9"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeWidth="2.5"
          />
        </svg>
        <div className="font-['Geist:Medium',_sans-serif] text-[14px] leading-[20px] text-[#605d66]">
          {message}
        </div>
      </div>
    </ModalPanel>
  );
}

export function CurrencyBadge({ currency }: { currency: string }) {
  return (
    <span className="inline-flex h-6 items-center rounded-full bg-[#eff4ff] px-2.5 font-['Geist:Medium',_sans-serif] text-[12px] font-medium leading-[16px] text-[#2d58ff]">
      {currency.toUpperCase()}
    </span>
  );
}
